<template>
  <v-row
    no-gutters
    class="fill-height">
    <v-col class="px-6 pt-2 pb-12 fill-height">
      <h1 class="display-1">
        {{ $t("clients") }}
        <span>
          {{ clientsLength }} {{$t("total")}}
        <v-btn
          icon
          small
          color="textColor"
          class="refresh"
          @click="refreshData()"
          >
          <v-icon small>mdi-autorenew</v-icon>
        </v-btn>
      </span>
      </h1>
      <h2 class="subtitle-2">{{ $t("clientsSubtitle") }}</h2>
      <div class="pt-5 pb-5 d-flex align-end justify-space-between">
        <v-col
          class="pb-0"
          cols="5">
          <v-text-field
            v-model="model.search"
            append-icon="mdi-magnify"
            :label="searchLabel"
            single-line
            clearable
            hide-details
            @input="onSearchChange"
          />
        </v-col>
        <div
          class="pb-0"
          cols="4">
          <v-btn 
            color="primary"
            class="#e4e4e9--text"
            @click="addClient"
            rounded>{{ $t("clientAdd") }}</v-btn>
        </div>
      </div>
      <v-sheet class="table" height="800">
      <v-data-table
        :headers="tableheaders"
        :items="clients"
        :loading="isLoadingTable"
        :loading-text="$t('loadingData')"
        v-model="model.selected"
        fixed-header
        max-height="550px"
        item-key="tag"
        class="clients-table max-height elevation-1"
        @click:row="selectClient"
        :footer-props="{ 'items-per-page-options': [5, 10, 15, 50] }"
      >
      <template v-slot:[`item.fullname`] ="{item}">
        {{ item.is_business ? `${item.entity_name} (${item.fullname})` : `${item.fullname}` }}
      </template>
        <template v-slot:[`item.is_premium`] ="{item}">
          <v-icon
            v-if="item.is_premium"
            color="success">
            mdi-star-box-outline
          </v-icon>
          
        </template>
        <template v-slot:[`item.is_business`] ="{item}">
          <v-icon
          color="gray">
          {{ item.is_business ? 'mdi-domain' : 'mdi-account' }}
        </v-icon>

        </template>
        <template v-slot:[`item.phone`] ="{item}">
          <template v-if="item.contacts_length && item.contacts_length > 3">
            <v-tooltip max-width="250px" top class="pt-1 grayBold--text text-truncate" style="max-width: 180px;">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="text-truncate" style="max-width: 250px;">
                  {{ item.contacts }}
                </div>
              </template>
              <span style="color:black">{{ item.contacts }}</span>
            </v-tooltip>
          </template>
          <template v-else>
            {{ item.contacts  }}
          </template>
        </template>
        <template v-slot:[`item.created_at`] ="{item}">
          {{ formatDate(item.created_at) }}
        </template>
        <template v-slot:[`item.notes`]="{ item }">
        <v-tooltip top v-if="item.notes && item.notes.length >= 18">
          <template v-slot:activator="{ on }">
            <div class="text-truncate" style="max-width: 130px;" v-on="on">{{ item.notes }}</div>
          </template>
          <span class="spanWordWrap" style="color:black">{{ item.notes }}</span>
        </v-tooltip>
        <div v-else>{{ item.notes && item.notes.length > 0 ? item.notes : '-' }}</div>
      </template>
      <template v-slot:[`item.email`] ="{item}">
          {{ item.email && item.email.length > 0 ? item.email : '-' }}
        </template>
        <template v-slot:no-data>
          {{ $t("clientsEmpty") }}
        </template>
        <!-- does not use deviceInfo, but it needs to be attached to a value in devices, loggedIn is already used -->
        <template v-slot:[`item.id`] ="{item}">
          <Dropdown>
            <DropdownItem
              title="view"
              @clicked="selectClient(item)"/>
            <DropdownItem
              title="edit"
              @clicked="editClient(item)"/>
            <DropdownItem
              title="delete"
              @clicked="deleteClient(item)"/>
          </Dropdown>
        </template>
      </v-data-table>
    </v-sheet>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { commonModule, clientsModule, installationsModule } from '@/store/modules/store-accessor'
import Dropdown from '../components/dropdown/Dropdown.vue'
import DropdownItem from '../components/dropdown/DropdownItem.vue'
import { convertEnglishToGreek, sortTableDates } from "@/helpers/data";
import { TimeUtil } from '@/helpers/TimeUtil';

@Component({
  components: { Dropdown, DropdownItem }
})
export default class Clients extends Vue {
  tableheaders: any [] = [
    { text: '', align: 'left', class: 'row-style', sortable: false, value: 'is_business' },
    { text: this.$t("companyName"), sortable: true, class: 'row-style', value: 'fullname' },
    { text: this.$t("phone"), align: 'left', sortable: true, class: 'row-style', value: 'phone' },
    { text: this.$t("notes"), align: 'left', sortable: true, class: 'row-style', value: 'notes' },
    { text: this.$t("created"), sortable: true, class: 'row-style', value: 'created_at', sort: (a: string, b: string) => sortTableDates(a, b) },
    { text: this.$t("email"), sortable: true, class: 'row-style', value: 'email' },
    { text: '', align: 'right', sortable: false, class: 'row-style', value: 'id' } 
    ]

  model: any = {
    search: '',
    selected: [] as string[]
  }

  timer: number;
  query: any;

   onSearchChange(val: string) {
    clearTimeout(this.timer)

    this.timer = window.setTimeout(async () => {
      clientsModule.setIsLoadingTable(true);
      let params = new URLSearchParams();
      if (val && val.length > 0) {
        val = convertEnglishToGreek(val);
        clientsModule.setSearch(val);
        clientsModule.queryObject.search = clientsModule.search;
      } else {
        clientsModule.setSearch('');
        delete clientsModule.queryObject.search;
      }
      this.$router.replace({ query: clientsModule.queryObject });
      
      // Check if user selected any of the queries
      const queryIndex = this.$route.fullPath.indexOf('?');
      if(queryIndex >= 0) {
        const query = this.$route.fullPath.substring(queryIndex);
        clientsModule.setFilters(query);
      } else {
        clientsModule.setFilters("");
      }

      await clientsModule.getClientsAction(clientsModule.filters);
      clientsModule.setIsLoadingTable(false);
    }, 500);
  }

  get clientsLength() {
    return this.clients.length
  }

  get searchLabel() {
    return this.$t("clientsSearch")
  }

  get clients(){
    return clientsModule.clients
  }

  get isLoadingTable() {
    return clientsModule.isLoadingTable;
  }

  get currClient(){
    return clientsModule.currentClient
  }

  formatDate (date: Date): string {
    return TimeUtil.formatDate(date)
  }

  async mounted () {
    /*
     TODO: Handle clients properly when UX is clear.
     */
    clientsModule.getClientsCount();
    clientsModule.getClientsAction('');
    clientsModule.setFilters('');
    clientsModule.setSearch('');
    clientsModule.setQueryObject({});
    commonModule.getContactTypeCombo();
    commonModule.initSnackbar({});
  }

  destroyed() {
    clientsModule.clearClients();
  }

  async selectClient (client: any) {
    try {
      clientsModule.setIsLoadingTable(true);
      await clientsModule.getClientsInfo(client.id)
      commonModule.showSideview({ name: 'clients-sideview', payload: { clientId: client.id } })
    } catch (error) {
      clientsModule.setIsLoadingTable(false);
    }
  }

  addClient (){
    commonModule.showModal({ name: 'add-client-modal' })
  }

  async deleteClient (item: any){
    await installationsModule.getInstallationsByIdAction(item.id);
    commonModule.showModal({ name: 'delete-client-modal', payload: { client: item}})
  }

  async editClient (client: any){
    await clientsModule.getClientsInfo(client.id)
    commonModule.showModal({ name: 'edit-client-modal', payload: { client: client, view: "clients"}})
  }

  async refreshData() {
    await clientsModule.getClientsCount();
   await clientsModule.getClientsAction(clientsModule.filters);
  }
}
</script>

<style scoped lang="scss">

.client-table:not(thead) {
  cursor: pointer;
  min-width: 600px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}


.max-height {
  max-height: 65vh;
  overflow: auto;
}

.placeholder {
  background-color: grey
}

h1 {
  margin-top: 40px;
  font-size: 35px;
  font-weight: 500;
  line-height: 65px;
}

span {
  margin-left: 10px;
  font-size: 18px;
  font-weight: 400;
  color: $pale-purple;
}

.v-tooltip__content {
  background-color: white;
  color: black;
  font-size: 14px;
  font-family: Arial, sans-serif;
  border-style: solid ;
  border-color: #777777;
  border-width: 1px;
}

.spanWordWrap {
  display: block;
  width: 200px;
  word-wrap: break-word;
}

.refresh {
  margin-bottom: 3px;
}
</style>
